import { type MutationResult, useMutation, useQuery } from '@apollo/client';
import type {
  PrivacyPolicyQuestion,
  Qualification,
  Role,
} from '@seek/ca-graphql-schema/types';
import { sortRoles } from '@seek/ca-myprofile-utils';
import { useMelwaysInfo } from '@seek/melways-react';
import { metrics } from '@seek/metrics-js';
import { useTranslations } from '@vocab/react';
import {
  Box,
  Button,
  Divider,
  IconSend,
  Inline,
  Stack,
} from 'braid-design-system';
import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useFormContext } from 'react-hook-form';
import { useLocation, useNavigate } from 'react-router-dom';

import { gql } from 'src/__generated__';
import type {
  ApplySubmitApplicationMutation,
  CoverLetterInput,
  Resume,
  SelectionCriteriaInput,
  SubmitApplicationFailure,
  SubmitApplicationInput,
  VisibilityLevel,
} from 'src/__generated__/graphql';
import { useQueryLicences } from 'src/components/Licences/hooks/useQueryLicences.tsx';
import PersonalDetails from 'src/components/PersonalDetails';
import { ROLE_REQUIREMENT_NAMESPACE } from 'src/components/RoleRequirementsForm/RoleRequirementsForm';
import {
  isFreeTextQuestion,
  isSingleChoiceQuestion,
  questionIdToFieldKey,
} from 'src/components/RoleRequirementsForm/utils';
import { useConfig } from 'src/context/config';
import { Page, PageSection } from 'src/context/tracking';
import { useApplicationCorrelationId } from 'src/context/use-application-correlation-id';
import { useLayoutEffectOnce } from 'src/context/use-layout-effect-once';
import { useJobDetails } from 'src/context/useJobDetails/useJobDetails.tsx';
import { useJobId } from 'src/context/useJobId';
import { FileAttachment } from 'src/forms/common/AttachmentSelect/AttachmentSelect';
import { useAllQuestions } from 'src/hooks/useAllQuestions/useAllQuestions';
import { logError, logInfo } from 'src/modules/error-logger/browserLog';
import { SUBMIT_APPLICATION } from 'src/modules/graphql-client/mutations/submitApplication.graphql';
import { getOperationNameFromQuery } from 'src/modules/utils';
import type { DocumentsFormValues } from 'src/pages/Documents/Documents';
import { useDocuments } from 'src/pages/Documents/hooks/useDocuments';
import { useDocumentsMutations } from 'src/pages/Documents/hooks/useDocumentsMutations';
import { useUnconfirmedData } from 'src/pages/Profile/hooks/useUnconfirmedData.tsx';
import { AlertMessage } from 'src/ui/AlertMessage/AlertMessage';
import PageLoader from 'src/ui/PageLoader/PageLoader';
import { ScrollOnMount } from 'src/ui/ScrollOnMount/ScrollOnMount.tsx';

import { CoverLetterItem } from './DocumentsSummary/CoverLetterItem';
import DocumentsSummary from './DocumentsSummary/DocumentsSummary';
import { ResumeItem } from './DocumentsSummary/ResumeItem';
import { SelectionCriteriaItem } from './DocumentsSummary/SelectionCriteriaItem';
import { PrivacyPolicy } from './PrivacyPolicy.tsx';
import ProfileSummary from './ProfileSummary/ProfileSummary';
import { ProfileVisibilityInput } from './ProfileVisibility';
import translations from './ReviewPage.vocab';
import { PrivacyPolicyInput } from './RoleRequirementsSummary/PrivacyPolicyInput';
import RoleRequirementsSummary from './RoleRequirementsSummary/RoleRequirementsSummary';
import { useTracking } from './hooks/useTracking';

export const REVIEW_PAGE = gql(`
  query ReviewPage(
    $jobId: ID!
    $zone: Zone!
    $languageCode: LanguageCodeIso!
  ) {
    viewer {
      _id
      profileVisibility {
        level
      }
      roles {
        ...role
      }
      skills {
        ...skill
      }
      qualifications(
        includeVerified: true
        status: confirmed
        zone: $zone
        languageCode: $languageCode
      ) {
        ...qualification
      }
    }
    jobApplicationProcess(jobId: $jobId) {
      documents {
        selectionCriteriaRequired
      }
    }
  }
`);

interface PrivacyPolicyInput {
  id: string;
  value: boolean;
}

interface Props {
  formPayload: Partial<SubmitApplicationInput>;
  isNewToWorkforce: boolean;
  onNewToWorkforceToggle: (toggle: boolean) => void;
}

interface ReviewFormValues extends DocumentsFormValues {
  profilePrivacyLevel: VisibilityLevel;
  privacyPolicy?: PrivacyPolicyInput;
  [ROLE_REQUIREMENT_NAMESPACE]: Record<string, string | string[]>;
}

const ReviewPage = ({
  formPayload,
  isNewToWorkforce,
  onNewToWorkforceToggle,
}: Props) => {
  const jobId = useJobId();
  const { config } = useConfig();
  const { language } = useMelwaysInfo();
  const { t } = useTranslations(translations);
  const navigate = useNavigate();
  const location = useLocation();
  const { applicationCorrelationId } = useApplicationCorrelationId();

  const {
    data: { licences },
  } = useQueryLicences();

  const [submitApplication, submitState] = useMutation(SUBMIT_APPLICATION);

  const { resumes, shouldRenderselectionCriteria } = useDocuments();
  const {
    handleSubmit,
    control,
    watch,
    reset: resetRoot,
    getValues: getRootValues,
    setValue,
    getValues,
    clearErrors,
    formState,
  } = useFormContext<ReviewFormValues>();

  const { data, loading } = useQuery(REVIEW_PAGE, {
    variables: { jobId, zone: config.ZONE, languageCode: language },
    onError(e) {
      logError(
        {
          type: 'review-page-graphql',
          message: 'Failed to fetch profile privacy level',
        },
        {
          error: e,
          operationName: getOperationNameFromQuery(REVIEW_PAGE),
        },
      );
    },
  });

  const profilePrivacyLevel = data?.viewer?.profileVisibility?.level;
  useEffect(() => {
    if (profilePrivacyLevel) {
      setValue('profilePrivacyLevel', profilePrivacyLevel);
    }
  }, [profilePrivacyLevel, setValue]);

  const { generateCoverletterUri, generateSelectionCriteriaUri } =
    useDocumentsMutations();
  const rootCoverLetterMethod = watch('coverLetter-method');
  const rootCoverLetterFile = watch('coverLetter-file');
  const rootCoverLetterText = watch('coverLetter-text');
  const rootSelectionCriteriaMethod = watch('selectionCriteria-method');
  const rootSelectionCriteriaFile = watch('selectionCriteria-file');
  const rootSelectionCriteriaText = watch('selectionCriteria-text');

  const defaultResumeId = watch('resume-select');
  const resume =
    watch('resume-method') !== FileAttachment.NONE
      ? resumes.find((item) => item.id === defaultResumeId)
      : null;

  const { trackSubmitClick, trackSubmitSucceeded } = useTracking({
    currentPage: Page.REVIEW_PAGE,
    pageSection: PageSection.REVIEW_APPLICATION,
    isCandidateNewToWorkforce: isNewToWorkforce,
  });

  const { data: jobDetails } = useJobDetails();
  const [pageViewedFired, setPageViewedFired] = useState(false);
  const advertiser = jobDetails?.advertiser?.name || '';

  const unconfirmedData = useUnconfirmedData({
    skip: Boolean(formPayload.resume),
  });

  useLayoutEffectOnce(() => {
    setPageViewedFired(true);
  });

  const { ZONE: zone } = config;

  const { data: questionsWithOptionIds } = useAllQuestions();
  const showQuestionsSummary = Boolean(questionsWithOptionIds.length);

  const submitForm = handleSubmit(async (formValues) => {
    // @ts-ignore Role type slightly different to types from myprofile-utils
    const latestRole = sortRoles<Role>(data?.viewer.roles || [])[0];
    const mostRecentRole = latestRole
      ? {
          company: latestRole.company.text,
          title: latestRole.title.text,
          started: latestRole.from,
          finished: latestRole.to || undefined,
        }
      : undefined;

    const questionnaire: Record<string, string | string[]> = getRootValues(
      ROLE_REQUIREMENT_NAMESPACE,
    );

    const questionnaireAnswers = Object.entries(questionnaire).map(
      ([id, value]) => {
        const question = questionsWithOptionIds.find(
          (q) => questionIdToFieldKey(q.id) === id,
        );

        if (isFreeTextQuestion(question)) {
          return {
            questionId: question?.id || '',
            answers: [{ text: value as string }],
          };
        }

        const options = question?.options || [];

        if (isSingleChoiceQuestion(question)) {
          return {
            questionId: question?.id || '',
            answers: options.filter((o) => value === o.id),
          };
        }

        return {
          questionId: question?.id || '',
          answers: options.filter((o) => value.includes(o.id)),
        };
      },
    );

    if (formValues.privacyPolicy) {
      const privacyPolicyQuestion = questionsWithOptionIds.find(
        ({ id }) => id === formValues.privacyPolicy!.id,
      ) as unknown as PrivacyPolicyQuestion;

      const privacyPolicyInput = {
        questionId: formValues.privacyPolicy.id,
        answers: privacyPolicyQuestion.options.filter(({ text }) => {
          const yesNo = formValues.privacyPolicy!.value ? 'Yes' : 'No';
          return text === yesNo;
        }),
      };
      questionnaireAnswers?.push(privacyPolicyInput);
    }

    const resumeInput = resume
      ? {
          id: resume?.id,
          uri: resume?.fileMetadata?.uri || '',
          idFromResumeResource: -1,
        }
      : null;

    type InputMaybe<T> = T | null | undefined;
    let coverLetterInput: InputMaybe<CoverLetterInput> = null;

    if (rootCoverLetterMethod === 'upload') {
      const uploadCoverLetter: {
        uri: string;
      } = {
        uri: await generateCoverletterUri(rootCoverLetterFile as File),
      };

      coverLetterInput = uploadCoverLetter;
    }
    if (rootCoverLetterMethod === 'change') {
      coverLetterInput = {
        writtenText: rootCoverLetterText,
        uri: await generateCoverletterUri(rootCoverLetterText as string),
      };
    }

    let selectionCeriteriaInput: InputMaybe<SelectionCriteriaInput> = null;

    if (rootSelectionCriteriaMethod === 'upload') {
      const uploadSelectionCriteria: {
        uri: string;
      } = {
        uri: await generateSelectionCriteriaUri(
          rootSelectionCriteriaFile as File,
        ),
      };

      selectionCeriteriaInput = uploadSelectionCriteria;
    }

    if (rootSelectionCriteriaMethod === 'change') {
      selectionCeriteriaInput = {
        writtenText: rootSelectionCriteriaText,
        uri: await generateSelectionCriteriaUri(
          rootSelectionCriteriaText as string,
        ),
      };
    }

    try {
      const response = await submitApplication({
        variables: {
          input: {
            jobId,
            correlationId: applicationCorrelationId,
            zone,
            // TODO: formValues.profilePrivacyLevel causes tests to fail
            profilePrivacyLevel: getValues('profilePrivacyLevel'),
            ...(resumeInput !== null && { resume: resumeInput }),
            ...(coverLetterInput !== null && { coverLetter: coverLetterInput }),
            ...(selectionCeriteriaInput !== null && {
              selectionCriteria: selectionCeriteriaInput,
            }),
            mostRecentRole,
            questionnaireAnswers:
              questionnaireAnswers.length > 0
                ? questionnaireAnswers
                : undefined,
          },
          locale: config.VOCAB_LANGUAGE,
        },
      });

      if (
        response.data?.submitApplication.__typename ===
        'SubmitApplicationFailure'
      ) {
        // should be an error on: __typename: 'SubmitApplicationValidationError',
        const errorMessage =
          response.data?.submitApplication.errors.length > 0 &&
          response.data?.submitApplication.errors.map((x) => x.message)[0];

        logError(
          {
            type: 'submit-application-validation-graphql',
            message:
              errorMessage ||
              'Submit application failed due to validation errors.',
          },
          {
            response,
            applicationCorrelationId,
            jobId,
            operationName: getOperationNameFromQuery(SUBMIT_APPLICATION),
          },
        );
        throw new Error('SubmitApplicationFailure');
      }

      const applicationId =
        response.data?.submitApplication.__typename ===
        'SubmitApplicationSuccess'
          ? response.data?.submitApplication.applicationId || ''
          : '';

      trackSubmitSucceeded({
        profileVisibilityLevel: getValues(
          'profilePrivacyLevel',
        ) as VisibilityLevel,
        applicationId,
        applicationCorrelationId,
        isResumeAttached: watch('resume-method') !== FileAttachment.NONE,
        isCoverLetterAttached:
          watch('coverLetter-method') !== FileAttachment.NONE,
        resumeHasSensitivePersonalInfo: Boolean(
          resume?.fileMetadata?.sensitiveDataInfo?.isDetected,
        ),
      });

      const successUrl = `../success`;
      navigate(
        { pathname: successUrl, search: location.search },
        { replace: true },
      );

      logInfo(
        `Successful Application ${applicationCorrelationId}: jobId ${jobId}`,
        'submit-application-success',
        {
          correlationId: applicationCorrelationId,
          jobId,
        },
      );
    } catch (e) {
      metrics.count('SUBMIT_APPLICATION_ERROR');
      logError(
        {
          type: 'submit-application-error',
          message: `for Application ${applicationCorrelationId}: jobId ${jobId}`,
        },
        {
          correlationId: applicationCorrelationId,
          jobId,
          error: e,
          operationName: getOperationNameFromQuery(SUBMIT_APPLICATION),
        },
      );
    }
  });

  const handleDocumentsChange = (values: Partial<DocumentsFormValues>) => {
    resetRoot({ ...getRootValues(), ...values }, { keepErrors: true });
  };

  const submitErrorMsg = getErrorMsg(t, submitState);

  const fieldNameTranslations = {
    resume: t('Resume'),
    coverLetter: t('Cover letter'),
    selectionCriteria: t('Selection criteria'),
    privacyPolicy: '', // no field name for privacy policy
    profilePrivacyLevel: '', //  profile privacy level will always be set
  };

  const validationErrors = Object.entries(formState.errors);
  const validationMessages = validationErrors.reduce((map, [key, error]) => {
    const prefix = key.split('-')[0] as keyof typeof fieldNameTranslations;
    const fieldName = fieldNameTranslations[prefix];
    if (!fieldName || !error) return map;
    return { ...map, [fieldName]: error.message };
  }, {});

  const hasErrors = Boolean(submitErrorMsg) || validationErrors.length > 0;

  return (
    <>
      <Helmet>
        <title>{`${t('Review and submit')} | ${config.BRAND}`}</title>
      </Helmet>
      {loading ? (
        <PageLoader />
      ) : (
        <Stack space="xxlarge">
          {hasErrors && (
            // adding key to force re-mount to trigger scroll
            <ScrollOnMount key={formState.submitCount}>
              <AlertMessage message={submitErrorMsg || validationMessages} />
            </ScrollOnMount>
          )}

          <Box data={{ testid: 'personal-details-summary' }}>
            <PersonalDetails />
          </Box>

          <DocumentsSummary>
            <ResumeItem
              resume={resume as Resume}
              resumes={resumes as Resume[]}
              onChange={handleDocumentsChange}
            />
            <Divider />
            <CoverLetterItem onChange={handleDocumentsChange} />
            {shouldRenderselectionCriteria && (
              <>
                <Divider />
                <SelectionCriteriaItem onChange={handleDocumentsChange} />
              </>
            )}
            <Divider />
          </DocumentsSummary>

          {showQuestionsSummary && (
            <Stack space="large">
              <RoleRequirementsSummary />
              <PrivacyPolicyInput
                pageViewedFired={pageViewedFired}
                privacyPolicyMessage={
                  formState.errors.privacyPolicy?.message || ''
                }
                control={control}
                clearErrors={clearErrors}
                setValue={setValue}
              />
            </Stack>
          )}

          <Box data={{ testid: 'profile-summary' }}>
            <ProfileSummary
              isNewToWorkforce={isNewToWorkforce}
              onNewToWorkforceToggle={onNewToWorkforceToggle}
              profilePrivacyLevel={watch('profilePrivacyLevel')}
              skills={data?.viewer?.skills}
              roles={data?.viewer?.roles || []}
              unconfirmedRoles={unconfirmedData.roles}
              qualifications={data?.viewer?.qualifications as Qualification[]}
              unconfirmedQualifications={unconfirmedData.qualifications}
              licences={licences}
              advertiser={advertiser}
            />
          </Box>

          {!data?.viewer?.profileVisibility?.level && (
            <Box data={{ testid: 'profile-privacy' }}>
              <ProfileVisibilityInput
                control={control}
                privacyLevelMessage={
                  formState.errors.profilePrivacyLevel?.message
                }
              />
            </Box>
          )}

          <Stack space="large">
            <PrivacyPolicy />
            <Inline space="none">
              <Button
                data={{ testid: `review-submit-application` }}
                type="submit"
                tone="brandAccent"
                loading={formState.isSubmitting}
                icon={<IconSend />}
                onClick={() => {
                  const profileVisibilityLevel = getValues(
                    'profilePrivacyLevel',
                  );
                  trackSubmitClick({ profileVisibilityLevel });
                  submitForm();
                }}
              >
                {t('Submit application')}
              </Button>
            </Inline>
          </Stack>
        </Stack>
      )}
    </>
  );
};

function isSubmitApplicationFailure(
  mutationResult: any,
): mutationResult is SubmitApplicationFailure {
  if (!mutationResult) return false;
  return mutationResult.__typename === 'SubmitApplicationFailure';
}

function getErrorMsg(
  t: (key: 'Sorry we couldnt') => string,
  { data, error }: MutationResult<ApplySubmitApplicationMutation>,
) {
  if (isSubmitApplicationFailure(data?.submitApplication)) {
    return data.submitApplication.errors?.map((x) => x.message)[0];
  }
  if (error) {
    return t('Sorry we couldnt');
  }
  return null;
}

export default ReviewPage;
